import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ParkingChartComponent from "../components/charts/ParkingChartComponent";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { useQueue } from "@/context/QueueContext";
import { processParams } from "../utils/paramsHelper";

// Page 14 Colors from index.css
const COLORS = {
  chartBlueLight: "#589CDB",
  chartOrangeLi: "#EF7521",
  chartGray: "#9A9A9A",
  tableBlueLight: "#9BC2E6",
  tableGrayDark: "#404040",
  navyDark: "#002060",
  grayBg: "#D9D9D9",
  darkGray: "#4A4A4A",
  white: "#FFFFFF",
  black: "#000000",
  lightBorder: "#CCCCCC",
};

const cleanVal = (val) => (!val || val === "-") ? "0" : val;

export default function KpiChart14({year = "",month = "",monthNumber = "",contract = "",page = null, isRefresh = false}) {
  const { enqueue } = useQueue();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [top5Data, setTop5Data] = useState([]);
  const [bottomTableData, setBottomTableData] = useState([]);
  const [years, setYears] = useState([]);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchParkingData();
  }, [year, month, contract]);

  const fetchParkingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() => apiCall(
        "GET",
        "/boardpack/transaction-by-duration",
        apiParams,
        navigate,
                false,
                false,
                isRefresh
      ));

      if (response?.success && response?.data) {
        const apiYears = response.meta?.years || [];
        setYears(apiYears);

        const tableData = response.data.table["Ticket_Count"];

        const firstYear = apiYears[0]?.toString();
        const durations = Object.keys(tableData[firstYear] || {});

        const sortedDurations = [...durations].sort((a, b) => parseFloat(a) - parseFloat(b));

        // Chart data - REVERSED for bottom-to-top display
        const processedChartData = sortedDurations
          .map((duration) => {
            const dataPoint = { duration };
            apiYears.forEach((year) => {
              const yearKey = year.toString();
              dataPoint[`y${year}`] = parseInt(
                cleanVal(tableData[yearKey]?.[duration]).replace(/,/g, "")
              );
            });
            return dataPoint;
          })
          .reverse();

        // Top5 data
        const processedTop5 = [];
        const maxLength = Math.max(
          ...apiYears.map((year) => response.data.top5[year.toString()]?.length || 0)
        );

        for (let i = 0; i < maxLength; i++) {
          const row = {};
          apiYears.forEach((year) => {
            const yearKey = year.toString();
            row[`time${year}`] = response.data.top5[yearKey]?.[i]?.duration || "";
            row[`value${year}`] = cleanVal(response.data.top5[yearKey]?.[i]?.value);
          });
          processedTop5.push(row);
        }

        // Bottom table data - NO REVERSE
        const processedBottomTable = sortedDurations.map((duration) => {
          const row = { duration };
          apiYears.forEach((year) => {
            const yearKey = year.toString();
            row[`y${year}`] = cleanVal(tableData[yearKey]?.[duration]);
          });
          return row;
        });

        setChartData(processedChartData);
        setTop5Data(processedTop5);
        setBottomTableData(processedBottomTable);
      } else {
        setError("No data available");
      }
    } catch (error) {
      console.error("Error fetching parking data:", error);
      setError(error.message || "Failed to fetch parking data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchParkingData} />;
  }

  return (
    <ParkingChartComponent
      chartData={chartData}
      top5Data={top5Data}
      bottomTableData={bottomTableData}
      years={years}
      colors={COLORS}
      yAxisLabel={page?.yAxisLabel}
      mainTitle={page?.title}
      subTitle={page?.subtitle}
      chartTitle={page?.in_chart_content_line_1}
      chartSubTitle={page?.in_chart_content_line_2}
      bottomLabel={page?.bottomLabel}
    />
  );
}